import { useMemo } from 'react'
import { useAvailableInstances, useAvailableModules } from '@syscolect/application'
import type { ModuleInstance } from '@syscolect/domain'
import type { AppModule } from './registry'

// ─── Pure helpers (no React) ──────────────────────────────────────────────────

/** Returns instances that have no parent (root of the tree). */
export function rootInstances(
  all: readonly ModuleInstance[],
): readonly ModuleInstance[] {
  return all.filter((i) => i.parentMid === undefined)
}

/** Returns direct children of a given parent instance id. */
export function childrenOf(
  all: readonly ModuleInstance[],
  parentMid: string,
): readonly ModuleInstance[] {
  return all.filter((i) => i.parentMid === parentMid)
}

export interface ModuleBreadcrumbEntry {
  id: string
  slug: string
  label: string
}

/**
 * Walks `parentMid` upward from `mid` and returns the breadcrumb chain
 * ordered root → current (inclusive).
 */
export function breadcrumbChain(
  all: readonly ModuleInstance[],
  modules: readonly AppModule[],
  mid: string,
): readonly ModuleBreadcrumbEntry[] {
  const byId = new Map(all.map((i) => [i.id, i]))
  const modulesBySlug = new Map(modules.map((m) => [m.slug, m]))

  const chain: ModuleBreadcrumbEntry[] = []
  let current = byId.get(mid)

  while (current) {
    const def = modulesBySlug.get(current.slug)
    chain.unshift({
      id: current.id,
      slug: current.slug,
      label: current.label ?? def?.label ?? current.slug,
    })
    current = current.parentMid ? byId.get(current.parentMid) : undefined
  }

  return chain
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Root-only instances (no parent). Used by WorkspacePage. */
export function useRootInstances(): readonly ModuleInstance[] {
  const all = useAvailableInstances()
  return useMemo(() => rootInstances(all), [all])
}

/** Direct children of a group instance. Used by GroupLandingPage + sidebar. */
export function useGroupChildren(
  parentMid: string | undefined,
): readonly ModuleInstance[] {
  const all = useAvailableInstances()
  return useMemo(
    () => (parentMid ? childrenOf(all, parentMid) : []),
    [all, parentMid],
  )
}

/** Breadcrumb chain for a module instance (root → current). */
export function useModuleBreadcrumbs(
  mid: string | undefined,
): readonly ModuleBreadcrumbEntry[] {
  const all = useAvailableInstances()
  const modules = useAvailableModules<AppModule>()
  return useMemo(
    () => (mid ? breadcrumbChain(all, modules, mid) : []),
    [all, modules, mid],
  )
}
